const { addToCart, config, on } = window.nt;

/**
 * <quick-add> — the "quick add" button of a product card.
 *
 * - A product with a single variant (data-variant-id) is added to the cart at once.
 * - A product with options opens one dialog, shared by all the cards of the page. Its content is the section of
 *   the alternate product template "quick-add", fetched on demand (and as soon as the visitor shows interest:
 *   pointer over the button or keyboard focus) and kept for the next opening. The custom elements the dialog
 *   needs are only loaded then, so a collection page that nobody quick-adds from does not pay for them.
 *
 * Without JavaScript the button is a plain link to the product page.
 */

const pages = new Map();
let dialog;
let opener;

/**
 * The alternate template "product.quick-add" has no page layout around it ("layout": false), so the response is
 * the section alone: a few KB, with nothing to cut out of a full page.
 */
async function fetchSection(productUrl) {
  const url = new URL(productUrl, location.origin);
  url.searchParams.set('view', 'quick-add');

  const response = await fetch(url, { headers: { 'X-Requested-With': 'XMLHttpRequest' } });
  if (!response.ok) throw new Error(`Request failed with status ${response.status}`);
  return response.text();
}

function loadSection(productUrl) {
  if (!pages.has(productUrl)) {
    pages.set(
      productUrl,
      fetchSection(productUrl).catch((error) => {
        pages.delete(productUrl);
        throw error;
      })
    );
  }
  return pages.get(productUrl);
}

function loadComponents() {
  // Versioned URLs from the layout: a relative path would skip the cache-busting query of the asset.
  return Promise.all(Object.values(config.modules).map((url) => import(url)));
}

function getDialog(closeLabel) {
  if (dialog) return dialog;

  dialog = document.createElement('dialog');
  dialog.className = 'qadd-dialog';

  const close = document.createElement('button');
  close.className = 'qadd-dialog__close';
  close.type = 'button';
  close.setAttribute('aria-label', closeLabel);
  close.innerHTML =
    '<svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" ' +
    'stroke-linecap="round" aria-hidden="true"><path d="M6 6l12 12M18 6L6 18"/></svg>';

  const body = document.createElement('div');
  body.className = 'qadd-dialog__body';
  dialog.append(close, body);

  // A click on the backdrop lands on the dialog element itself, since the content fills it.
  dialog.addEventListener('click', (event) => {
    if (event.target === dialog || event.target.closest('.qadd-dialog__close')) dialog.close();
  });
  dialog.addEventListener('close', () => {
    body.replaceChildren();
    // When the cart drawer has just opened, it already took the focus.
    if (!document.querySelector('cart-drawer dialog[open]')) opener?.focus();
  });
  document.body.append(dialog);

  // Once the item is in the cart, the dialog has done its job.
  on('cart:updated', () => {
    if (dialog.open) dialog.close();
  });

  return dialog;
}

class QuickAdd extends HTMLElement {
  connectedCallback() {
    this.button = this.querySelector('[data-quick-add-button]');
    this.status = this.querySelector('[data-status]');
    this.button?.addEventListener('click', this.onClick);
    this.button?.addEventListener('pointerenter', this.onIntent, { once: true });
    this.button?.addEventListener('focus', this.onIntent, { once: true });
  }

  disconnectedCallback() {
    this.button?.removeEventListener('click', this.onClick);
    this.button?.removeEventListener('pointerenter', this.onIntent);
    this.button?.removeEventListener('focus', this.onIntent);
  }

  onIntent = () => {
    if (!this.dataset.variantId) loadSection(this.dataset.productUrl).catch(() => {});
  };

  onClick = async (event) => {
    event.preventDefault();
    if (this.hasAttribute('aria-busy')) return;

    this.setBusy(true);
    this.announce('');
    try {
      if (this.dataset.variantId) await this.addNow();
      else await this.openDialog();
    } catch {
      // The product page can always do what the shortcut could not.
      location.href = this.dataset.productUrl;
    } finally {
      this.setBusy(false);
    }
  };

  async addNow() {
    const body = new FormData();
    body.append('id', this.dataset.variantId);
    body.append('quantity', '1');
    await addToCart(body, 'quick-add');
    this.announce(this.dataset.addedLabel);
  }

  async openDialog() {
    const [html] = await Promise.all([loadSection(this.dataset.productUrl), loadComponents()]);
    const section = new DOMParser().parseFromString(html, 'text/html').body;

    const target = getDialog(this.dataset.closeLabel);
    if (this.dataset.schemeId) target.dataset.scheme = this.dataset.schemeId;
    target.setAttribute('aria-label', this.dataset.productTitle ?? '');
    target.querySelector('.qadd-dialog__body').replaceChildren(...section.childNodes);

    opener = this.button;
    target.showModal();
  }

  setBusy(busy) {
    this.toggleAttribute('aria-busy', busy);
  }

  announce(message) {
    if (this.status) this.status.textContent = message;
  }
}

if (!customElements.get('quick-add')) customElements.define('quick-add', QuickAdd);
